import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { AttendanceSummaryResponse, ExceptionBuckets, ExceptionPerson, SummaryGroup } from '../../../services/attendanceSummaryService';
import {
  Page, Pill, SummaryLine,
  pdfColors, pdfSpacing, fmtClassName,
  type SchoolBranding, type OfficialDetail, type PdfTone,
} from '../../../utils/pdf';
import { STATUS_VISUAL } from '../../../utils/attendanceStatus';
import { AttendanceStatus } from '../../../types';

interface Props {
  summary: AttendanceSummaryResponse;
  scope: 'students' | 'employees';
  branding: SchoolBranding;
  generatedAt: string;
}

type Bucket = keyof ExceptionBuckets;

/** Bad news first — the order the eye scans an exception digest. */
const BUCKETS: Array<{ key: Bucket; label: string; tone: PdfTone }> = [
  { key: 'absent', label: 'Absent', tone: 'danger' },
  { key: 'late', label: 'Late', tone: 'warning' },
  { key: 'halfDay', label: 'Half day', tone: 'warning' },
  { key: 'leave', label: 'On leave', tone: 'primary' },
];

const styles = StyleSheet.create({
  group: {
    borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid', borderRadius: 4,
    marginBottom: pdfSpacing.sm,
  },
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6, paddingHorizontal: 10 },
  labels: { flexDirection: 'row', alignItems: 'baseline', flexShrink: 1 },
  primary: { fontSize: 10, fontWeight: 600, color: pdfColors.textPrimary },
  secondary: { fontSize: 8.5, color: pdfColors.textMuted, marginLeft: 6 },
  pills: { flexDirection: 'row', alignItems: 'center' },
  pillGap: { marginLeft: 4 },
  bucket: { paddingVertical: 6, paddingHorizontal: 10, borderTopWidth: 0.5, borderTopColor: pdfColors.divider, borderTopStyle: 'solid' },
  item: { flexDirection: 'row', alignItems: 'baseline', paddingVertical: 1.2 },
  idx: { width: 16, textAlign: 'right', marginRight: 6, fontSize: 7.5, color: pdfColors.textFaint },
  line: { flex: 1, fontSize: 8.5, color: pdfColors.textPrimary },
  name: { fontWeight: 600 },
  id: { fontSize: 7.5, color: pdfColors.textFaint },
  leave: { fontSize: 8, color: '#1e40af' },
  remarks: { fontSize: 8, color: pdfColors.textMuted, fontStyle: 'italic' },
  oneLiner: { paddingVertical: 6, paddingHorizontal: 10, fontSize: 8.5, borderTopWidth: 0.5, borderTopColor: pdfColors.divider, borderTopStyle: 'solid' },
  good: { backgroundColor: '#f0fdf4', color: '#166534' },
  muted: { color: pdfColors.textMuted, fontStyle: 'italic' },
  empty: {
    marginTop: pdfSpacing.lg, paddingVertical: 36, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted,
    borderWidth: 0.6, borderColor: pdfColors.dividerStrong, borderStyle: 'dashed', borderRadius: 4,
  },
});

const longDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

const Person: React.FC<{ person: ExceptionPerson; index: number; leave: boolean }> = ({ person, index, leave }) => (
  <View style={styles.item} wrap={false}>
    <Text style={styles.idx}>{index}.</Text>
    <Text style={styles.line}>
      <Text style={styles.name}>{person.name}</Text>
      {person.identifier ? <Text style={styles.id}>  ·  {person.identifier}</Text> : null}
      {leave && person.leaveType ? <Text style={styles.leave}>  ·  {person.leaveType}</Text> : null}
      {person.remarks ? <Text style={styles.remarks}>  —  {person.remarks}</Text> : null}
    </Text>
  </View>
);

const Group: React.FC<{ g: SummaryGroup; label: string }> = ({ g, label }) => (
  <View style={styles.group}>
    <View style={styles.head} wrap={false} minPresenceAhead={40}>
      <View style={styles.labels}>
        <Text style={styles.primary}>{label}</Text>
        {g.secondaryLabel ? <Text style={styles.secondary}>{g.secondaryLabel}</Text> : null}
      </View>
      <View style={styles.pills}>
        <Pill dense tone="success" label={`${g.presentCount}/${g.totalRoster} present`} />
        {g.exceptionCount > 0 && (
          <View style={styles.pillGap}><Pill dense tone="warning" label={`${g.exceptionCount} ${g.exceptionCount === 1 ? 'exception' : 'exceptions'}`} /></View>
        )}
        {g.notMarkedCount > 0 && !g.notMarked && (
          <View style={styles.pillGap}><Pill dense tone="neutral" label={`${g.notMarkedCount} not marked`} /></View>
        )}
      </View>
    </View>
    {g.allPresent ? (
      <Text style={[styles.oneLiner, styles.good]}>All present</Text>
    ) : g.notMarked ? (
      <Text style={[styles.oneLiner, styles.muted]}>Attendance not marked yet.</Text>
    ) : g.exceptionCount === 0 ? (
      <Text style={[styles.oneLiner, styles.muted]}>No exceptions so far.</Text>
    ) : (
      BUCKETS.filter(b => g.exceptions[b.key].length > 0).map(b => (
        <View key={b.key} style={styles.bucket}>
          <View style={{ alignSelf: 'flex-start', marginBottom: 3 }} minPresenceAhead={14}>
            <Pill dense tone={b.tone} label={`${b.label} · ${g.exceptions[b.key].length}`} />
          </View>
          {g.exceptions[b.key].map((p, i) => <Person key={p.id} person={p} index={i + 1} leave={b.key === 'leave'} />)}
        </View>
      ))
    )}
  </View>
);

/**
 * The daily attendance summary — exceptions first: for each class (or
 * role), who was absent, late, on a half day or on leave, under the
 * school's letterhead with the day's totals. Exactly what the summary
 * screen shows, printable.
 */
const AttendanceSummaryDocument: React.FC<Props> = ({ summary, scope, branding, generatedAt }) => {
  const date = longDate(summary.date);
  const who = scope === 'students' ? 'Students' : 'Employees';
  const details: OfficialDetail[] = [
    { value: who },
    { value: date },
    ...(summary.session?.name ? [{ label: 'Session', value: summary.session.name }] : []),
  ];
  const t = summary.totals;

  return (
    <Document title={`Daily Attendance Summary — ${date}`} author={branding.name}>
      <Page
        branding={branding}
        meta={{
          title: 'Daily Attendance Summary',
          subtitle: `${who}  ·  ${date}`,
          generatedAt,
        }}
        official={{ details }}
      >
        <SummaryLine
          groups={[{
            label: 'Today',
            items: [
              { label: scope === 'students' ? 'Classes' : 'Roles', value: t.rosters },
              { label: 'Present', value: t.present, color: STATUS_VISUAL[AttendanceStatus.PRESENT].bg },
              { label: t.exceptions === 1 ? 'Exception' : 'Exceptions', value: t.exceptions, color: STATUS_VISUAL[AttendanceStatus.LATE].bg },
              { label: 'Not marked', value: t.notMarked, color: pdfColors.dividerStrong },
            ],
          }]}
        />
        {summary.groups.length === 0
          ? <Text style={styles.empty}>No {scope} match this filter for {date}.</Text>
          : summary.groups.map(g => <Group key={g.key} g={g} label={scope === 'students' ? fmtClassName(g.primaryLabel) : g.primaryLabel} />)}
      </Page>
    </Document>
  );
};

export default AttendanceSummaryDocument;
